<template>
    <div :class="$style.initial">
        <div :class="$style.top_links">
            <BaseLink
                v-for="link in topBlockLinks"
                :key="link.title"
                :class="$style.top_link"
                :to="link.to"
                :externalTo="link.externalTo"
                :target="link.target"
                data-interface-target="search-top-link-mobile"
            >
                <Icon
                    v-if="link.icon"
                    :name="link.icon"
                    :class="$style.top_link_icon"
                />
                <Typography
                    size="m"
                    :responsive="false"
                    type="label"
                >
                    {{ link.title }}
                </Typography>
            </BaseLink>
        </div>
        <div :class="$style.categories">
            <BaseLink
                v-for="category in mainData.menuCategoriesList"
                :key="category.id"
                :to="category.location"
                data-interface-target="search-category-link-mobile"
                :class="$style.category"
            >
                <Icon
                    :name="category.id"
                    :class="$style.category_icon"
                />
                <Typography
                    size="s"
                    :class="$style.category_title"
                    :responsive="false"
                    type="label"
                >
                    {{ category.title }}
                </Typography>
            </BaseLink>
        </div>
        <div :class="$style.bottom_links">
            <BaseLink
                v-for="link in bottomBlockLinks"
                :key="link.title"
                :to="link.to"
                :externalTo="link.externalTo"
                :target="link.target"
                :class="$style.bottom_link"
                data-interface-target="search-bottom-link-mobile"
            >
                <Typography
                    size="m"
                    :responsive="false"
                    type="label"
                >
                    {{ link.title }}
                </Typography>
            </BaseLink>
        </div>

        <LocaleSwitchersList
            v-if="appStore.isAppEnv"
            :languageLinks="languageLinks"
        />
    </div>
</template>
<script setup lang="ts">
import { computed } from 'vue'
import { RouteLocationRaw } from 'vue-router'

import BaseLink from '@/components/BaseLink.vue'
import Icon, { IconNameType } from '@/components/Icon/Icon.vue'
import LocaleSwitchersList from '@/components/LocaleSwitchers/LocaleSwitchersList.vue'
import Typography from '@/components/Typography.vue'
import { useLocalizedRouters } from '@/composables/localized-routers'
import { useTr } from '@/composables/use-tr'
import { useGeneralData } from '@/query/use-general-data'
import { ROUTE_NAMES } from '@/router/constants'
import {
    BLOG_PATH,
    BUSINESS_LANDPAGE_PATH,
    DEVELOPERS_LANDPAGE_PATH,
    NAVIGATION_ALL_GAMES_LINKS,
} from '@/router/navigation-constants'
import { useAppStore } from '@/store/app-store'
import type { LinkTarget, MainCategoriesType } from '@/types'

const tr = useTr()
const { mainData } = useGeneralData()

const { getLocalizedUrls } = useLocalizedRouters()
const languageLinks = computed(() => getLocalizedUrls())
const appStore = useAppStore()

type Link = {
    title: string
    icon?: IconNameType
    to?: RouteLocationRaw
    externalTo?: string
    target?: LinkTarget
}

const topBlockLinks = computed<Link[]>(() => {
    const allGamesNavigation = NAVIGATION_ALL_GAMES_LINKS(tr)

    const getCategoryData = (id: MainCategoriesType) => {
        const category = mainData.value.contentCategoriesList.find((c) => c.id === id)
        return category
            ? ({
                  title: category.title,
                  icon: category.id,
                  to: category.location,
              } as Link)
            : null
    }

    return [
        allGamesNavigation.allGames,
        getCategoryData('trending_now'),
        allGamesNavigation.allSeries,
        getCategoryData('new'),
        allGamesNavigation.allTags,
        {
            title: tr('navigation.random'),
            icon: 'random',
            to: { name: ROUTE_NAMES.RANDOM_GAME },
        },
        {
            title: tr('navigation.add_game'),
            icon: 'plus',
            externalTo: DEVELOPERS_LANDPAGE_PATH,
            target: '_blank',
        },
    ].filter((l): l is Link => Boolean(l))
})

const bottomBlockLinks = computed<Link[]>(() => [
    {
        title: tr('navigation.for_developers'),
        externalTo: DEVELOPERS_LANDPAGE_PATH,
        target: '_blank',
    },
    {
        title: tr('navigation.blog'),
        externalTo: BLOG_PATH,
        target: '_blank',
    },
    {
        title: tr('navigation.for_business'),
        externalTo: BUSINESS_LANDPAGE_PATH,
        target: '_blank',
    },
    {
        title: tr('navigation.contact_us'),
        to: { name: ROUTE_NAMES.CONTACTS },
    },
    {
        title: tr('navigation.terms_of_use'),
        to: { name: ROUTE_NAMES.TERMS_OF_USE },
    },
])
</script>
<style module>
.initial {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.top_links {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
}

.top_link {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
}

.top_link_icon {
    width: 24px;
    height: 24px;
}

.categories {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
}

.category {
    display: flex;
    padding: 24px 16px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 16px;
    border-radius: 16px;
    background: var(--carbon-300);
}

.category_icon {
    width: 32px;
    height: 32px;
}

.category_title {
    text-align: center;
    word-break: break-word;
}

.bottom_links {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
}

.bottom_link {
    height: 24px;
    padding: 12px;
}
</style>
